import React from 'react';
import { CheckCircle, Circle, Clock } from 'lucide-react';
import KPICard from '../../components/shared/KPICard';
import SectionDivider from '../../components/shared/SectionDivider';
import GroupedBarChart from '../../components/charts/GroupedBarChart';
import { consumerMonthlyData, billingCycles } from '../../data/mockData';

interface SubsidyStep {
  step: number;
  label: string;
  detail?: string;
  state: 'done' | 'active' | 'pending';
}

const subsidySteps: SubsidyStep[] = [
  { step: 1, label: 'Registration', state: 'done' },
  { step: 2, label: 'Installation', state: 'done' },
  { step: 3, label: 'Generation Milestone', detail: '3,842 of 6,000 kWh target (64%)', state: 'active' },
  { step: 4, label: 'Disbursement', state: 'pending' },
];

export default function ConsumerBilling() {
  return (
    <div className="p-6 space-y-6">
      {/* KPIs */}
      <div className="flex gap-4">
        <div className="bg-consumer-amber rounded-card p-4 flex-1 border border-orange-200 shadow-card">
          <p className="text-xs text-orange-700 font-medium uppercase tracking-wide">Export This Month</p>
          <p className="text-xl font-bold text-text-primary mt-1">312 kWh</p>
          <p className="text-xs text-text-secondary mt-1">Apr 2026</p>
        </div>
        <div className="bg-consumer-amber rounded-card p-4 flex-1 border border-orange-200 shadow-card">
          <p className="text-xs text-orange-700 font-medium uppercase tracking-wide">Import This Month</p>
          <p className="text-xl font-bold text-text-primary mt-1">148 kWh</p>
          <p className="text-xs text-text-secondary mt-1">Apr 2026</p>
        </div>
        <div className="bg-consumer-amber rounded-card p-4 flex-1 border border-orange-200 shadow-card">
          <p className="text-xs text-orange-700 font-medium uppercase tracking-wide">Est. Credit</p>
          <p className="text-xl font-bold text-success mt-1">₹ 1,716</p>
          <p className="text-xs text-success mt-1">@ ₹5.50/kWh export rate</p>
        </div>
        <div className="bg-consumer-amber rounded-card p-4 flex-1 border border-orange-200 shadow-card">
          <p className="text-xs text-orange-700 font-medium uppercase tracking-wide">Net Bill Saving</p>
          <p className="text-xl font-bold text-success mt-1">₹ 2,340</p>
          <p className="text-xs text-success mt-1">vs same month last year</p>
        </div>
      </div>

      <div className="flex gap-4">
        {/* Grouped bar chart */}
        <div className="flex-1 bg-white rounded-card border border-border shadow-card p-4">
          <SectionDivider title="Monthly Generation vs Export" subtitle="Nov 2025 – Apr 2026 (kWh)" />
          <GroupedBarChart data={consumerMonthlyData} height={240} />
        </div>

        {/* Billing table + subsidy tracker */}
        <div className="w-80 flex-shrink-0 bg-white rounded-card border border-border shadow-card p-4 space-y-4">
          <div>
            <SectionDivider title="Billing Cycles" subtitle="Last 3 months" />
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-2 text-text-tertiary font-medium">Month</th>
                  <th className="text-right py-2 text-text-tertiary font-medium">Export kWh</th>
                  <th className="text-right py-2 text-text-tertiary font-medium">Import kWh</th>
                  <th className="text-right py-2 text-text-tertiary font-medium">Credit ₹</th>
                </tr>
              </thead>
              <tbody>
                {billingCycles.map(bc => (
                  <tr key={bc.month} className="border-b border-border/50 hover:bg-bg-secondary cursor-pointer">
                    <td className="py-2 text-text-secondary">{bc.month}</td>
                    <td className="py-2 text-right font-medium text-text-primary">{bc.exportKWh}</td>
                    <td className="py-2 text-right text-text-secondary">{bc.importKWh}</td>
                    <td className="py-2 text-right font-medium text-success">₹{bc.estimatedCredit.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-text-tertiary mt-2">* Credit is indicative. Actual settlement by DISCOM.</p>
          </div>

          <div className="border-t border-border pt-4">
            <SectionDivider title="Subsidy Tracker" />
            <div className="space-y-3">
              {subsidySteps.map((step, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    {step.state === 'done' ? (
                      <CheckCircle size={16} className="text-success" />
                    ) : step.state === 'active' ? (
                      <Clock size={16} className="text-warning" />
                    ) : (
                      <Circle size={16} className="text-text-tertiary" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className={`text-xs font-medium ${step.state === 'done' ? 'text-success' : step.state === 'active' ? 'text-warning' : 'text-text-tertiary'}`}>
                      Step {step.step}: {step.label}
                    </p>
                    {step.detail && (
                      <p className="text-xs text-text-secondary mt-0.5">{step.detail}</p>
                    )}
                    {step.state === 'active' && (
                      <div className="mt-1.5 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                        <div className="h-full bg-warning rounded-full" style={{ width: '64%' }} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
